import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import ProjectFilters from './components/ProjectFilters';
import ProjectModal from './components/ProjectModal';
import FeaturedProject from './components/FeaturedProject';
import ProjectStats from './components/ProjectStats';
import { realProjects } from '../../data/realProjects';

const ProjectsInnovationLaboratory = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    technology: [],
    type: [],
    industry: [],
    status: []
  });
  const [sortBy, setSortBy] = useState('newest');

  // Real Projects Data from CV
  const projects = realProjects || [
    {
      id: 1,
      title: "EcoCommerce Platform",
      description: "A comprehensive e-commerce platform focused on sustainable products with advanced analytics and AI-powered recommendations.",
      fullDescription: `EcoCommerce Platform is a revolutionary e-commerce solution designed specifically for sustainable and eco-friendly products. The platform combines modern web technologies with AI-powered recommendation systems to create a seamless shopping experience while promoting environmental consciousness.\n\nThe project features a comprehensive admin dashboard, real-time inventory management, integrated payment processing, and advanced analytics. Built with scalability in mind, it can handle thousands of concurrent users while maintaining optimal performance.`,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
      ],
      techStack: ["React", "Node.js", "MongoDB", "Express", "Stripe", "AWS", "Docker", "Redis"],
      type: "Web App",
      industry: "E-commerce",
      status: "Live",
      year: "2024",
      featured: true,
      liveUrl: "https://ecocommerce-demo.com",
      githubUrl: "https://github.com/lahiru/ecocommerce",
      metrics: [
        { label: "Active Users", value: "15K+" },
        { label: "Products Listed", value: "5K+" },
        { label: "Performance Score", value: "98%" },
        { label: "Uptime", value: "99.9%" }
      ],
      features: [
        "AI-powered product recommendations",
        "Real-time inventory management",
        "Advanced search and filtering",
        "Secure payment processing",
        "Mobile-responsive design",
        "Admin analytics dashboard"
      ],
      highlights: [
        "Increased user engagement by 40% through AI recommendations",
        "Achieved 98% Lighthouse performance score",
        "Processed over $500K in transactions",
        "Zero security incidents since launch"
      ],
      architecture: `The platform follows a microservices architecture with React frontend, Node.js backend services, and MongoDB for data persistence. Redis is used for caching and session management. The application is containerized with Docker and deployed on AWS with auto-scaling capabilities.`,
      challenges: [
        {
          problem: "Handling high traffic during flash sales without performance degradation",
          solution: "Implemented Redis caching, database query optimization, and AWS auto-scaling to handle traffic spikes up to 10x normal load"
        },
        {
          problem: "Complex product recommendation algorithm affecting page load times",
          solution: "Moved recommendation processing to background jobs and implemented smart caching strategies, reducing page load time by 60%"
        }
      ],
      testimonials: [
        {
          name: "Sarah Johnson",
          role: "Product Manager",
          avatar: "https://randomuser.me/api/portraits/women/32.jpg",
          content: "Lahiru delivered an exceptional e-commerce platform that exceeded our expectations. The AI recommendations have significantly boosted our sales.",
          rating: 5
        },
        {
          name: "Michael Chen",
          role: "CTO, GreenTech Solutions",
          avatar: "https://randomuser.me/api/portraits/men/45.jpg",
          content: "The technical architecture is solid and scalable. Lahiru\'s attention to performance optimization is remarkable.",
          rating: 5
        }
      ]
    },
    {
      id: 2,
      title: "HealthTracker Mobile App",
      description: "Cross-platform mobile application for health monitoring with real-time data synchronization and AI-powered insights.",
      fullDescription: `HealthTracker is a comprehensive mobile health application that empowers users to monitor their wellness journey through intuitive interfaces and smart analytics. The app integrates with various health devices and provides personalized insights based on user data patterns.\n\nBuilt with React Native for cross-platform compatibility, the app features offline functionality, secure data encryption, and seamless synchronization across devices. The backend utilizes machine learning algorithms to provide personalized health recommendations.`,
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop"
      ],
      techStack: ["React Native", "Python", "FastAPI", "PostgreSQL", "TensorFlow", "Firebase"],
      type: "Mobile App",
      industry: "Healthcare",
      status: "Live",
      year: "2024",
      featured: false,
      liveUrl: "https://healthtracker-app.com",
      githubUrl: "https://github.com/lahiru/healthtracker",
      metrics: [
        { label: "Downloads", value: "25K+" },
        { label: "Daily Active Users", value: "8K+" },
        { label: "App Store Rating", value: "4.8★" },
        { label: "Data Points Tracked", value: "1M+" }
      ],
      features: [
        "Real-time health data tracking",
        "AI-powered health insights",
        "Device integration (wearables)",
        "Offline data synchronization",
        "Personalized recommendations",
        "Progress visualization"
      ],
      highlights: [
        "Featured in App Store\'s \'Health & Fitness\' category",
        "Achieved 4.8-star rating with 1000+ reviews",
        "Integrated with 15+ popular health devices",
        "Processed over 1 million health data points"
      ],
      architecture: `Cross-platform mobile app built with React Native for iOS and Android. Backend services developed with Python FastAPI, PostgreSQL for data storage, and TensorFlow for ML-powered insights. Firebase handles push notifications and real-time updates.`,
      challenges: [
        {
          problem: "Ensuring data privacy and HIPAA compliance for health information",
          solution: "Implemented end-to-end encryption, secure authentication, and comprehensive audit logging to meet healthcare data protection standards"
        },
        {
          problem: "Synchronizing data across multiple devices while maintaining offline functionality",
          solution: "Developed a robust conflict resolution system and implemented incremental sync to ensure data consistency across all user devices"
        }
      ],
      testimonials: [
        {
          name: "Dr. Emily Rodriguez",
          role: "Healthcare Professional",
          avatar: "https://randomuser.me/api/portraits/women/28.jpg",
          content: "This app has revolutionized how my patients track their health metrics. The insights are incredibly valuable for treatment planning.",
          rating: 5
        }
      ]
    },
    {
      id: 3,
      title: "DataViz Analytics Dashboard",
      description: "Interactive data visualization platform with real-time analytics, custom chart builders, and collaborative features.",
      fullDescription: `DataViz Analytics Dashboard is a powerful business intelligence platform that transforms complex data into actionable insights through interactive visualizations. The platform supports multiple data sources and provides real-time analytics capabilities.\n\nDesigned for enterprise use, it features role-based access control, collaborative workspaces, and advanced filtering options. The dashboard builder allows users to create custom visualizations without coding knowledge while maintaining the flexibility for advanced users to create complex analytical views.`,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=600&fit=crop"
      ],
      techStack: ["Vue.js", "D3.js", "Python", "Django", "PostgreSQL", "Elasticsearch", "WebSocket"],
      type: "Web App",
      industry: "Analytics",
      status: "Completed",
      year: "2023",
      featured: false,
      liveUrl: "https://dataviz-demo.com",
      githubUrl: "https://github.com/lahiru/dataviz",
      metrics: [
        { label: "Enterprise Clients", value: "50+" },
        { label: "Data Points Processed", value: "10M+" },
        { label: "Custom Dashboards", value: "500+" },
        { label: "Query Response Time", value: "<200ms" }
      ],
      features: [
        "Drag-and-drop dashboard builder",
        "Real-time data streaming",
        "Multiple data source integration",
        "Advanced filtering and drilling",
        "Collaborative workspaces",
        "Export and sharing capabilities"
      ],
      highlights: [
        "Reduced report generation time by 80%",
        "Integrated with 20+ popular business tools",
        "Served 50+ enterprise clients",
        "Processed over 10 million data points daily"
      ],
      architecture: `Frontend built with Vue.js and D3.js for interactive visualizations. Backend powered by Django with PostgreSQL for structured data and Elasticsearch for fast querying. WebSocket connections enable real-time data updates.`,
      challenges: [
        {
          problem: "Handling large datasets while maintaining responsive user interface",
          solution: "Implemented data virtualization, progressive loading, and server-side filtering to handle millions of records without UI lag"
        },
        {
          problem: "Creating flexible visualization engine that supports various chart types",
          solution: "Developed a modular chart system using D3.js with reusable components and a plugin architecture for custom visualizations"
        }
      ],
      testimonials: [
        {
          name: "James Wilson",
          role: "Data Analyst, TechCorp",
          avatar: "https://randomuser.me/api/portraits/men/35.jpg",
          content: "The dashboard has transformed how we analyze our business data. The real-time capabilities are game-changing.",
          rating: 5
        }
      ]
    },
    {
      id: 4,
      title: "CommunityConnect Platform",
      description: "Social networking platform for local communities with event management, discussion forums, and volunteer coordination.",
      fullDescription: `CommunityConnect is a comprehensive social platform designed to strengthen local community bonds through digital connectivity. The platform facilitates neighborhood communication, event organization, and volunteer coordination.\n\nBuilt with modern web technologies, it features real-time messaging, event management, skill sharing, and local business directory. The platform emphasizes privacy and community safety with robust moderation tools and verification systems.`,
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1515378791036-0648a814c963?w=800&h=600&fit=crop"
      ],
      techStack: ["React", "Node.js", "Socket.io", "MongoDB", "Express", "Cloudinary", "JWT"],
      type: "Web App",
      industry: "Social",
      status: "Live",
      year: "2023",
      featured: false,
      liveUrl: "https://communityconnect.com",
      githubUrl: "https://github.com/lahiru/community-connect",
      metrics: [
        { label: "Active Communities", value: "200+" },
        { label: "Monthly Users", value: "12K+" },
        { label: "Events Organized", value: "1K+" },
        { label: "Volunteer Hours", value: "5K+" }
      ],
      features: [
        "Real-time community chat",
        "Event creation and management",
        "Volunteer opportunity matching",
        "Local business directory",
        "Skill sharing marketplace",
        "Community moderation tools"
      ],
      highlights: [
        "Connected 200+ local communities",
        "Facilitated 1000+ community events",
        "Coordinated 5000+ volunteer hours",
        "Achieved 85% user retention rate"
      ],
      architecture: `React-based frontend with Node.js backend using Express framework. MongoDB for data storage, Socket.io for real-time features, and Cloudinary for media management. JWT authentication ensures secure user sessions.`,
      challenges: [
        {
          problem: "Scaling real-time messaging across multiple communities without performance issues",
          solution: "Implemented room-based Socket.io architecture with Redis adapter for horizontal scaling and message persistence"
        },
        {
          problem: "Ensuring community safety while maintaining user privacy",
          solution: "Developed AI-powered content moderation system with human oversight and implemented privacy-first design principles"
        }
      ],
      testimonials: [
        {
          name: "Maria Garcia",
          role: "Community Leader",
          avatar: "https://randomuser.me/api/portraits/women/42.jpg",
          content: "This platform has brought our neighborhood together like never before. The event organization features are fantastic.",
          rating: 5
        }
      ]
    },
    {
      id: 5,
      title: "FinanceTracker API",
      description: "RESTful API for personal finance management with advanced security, real-time transactions, and banking integrations.",
      fullDescription: `FinanceTracker API is a robust backend service designed to power personal finance applications with enterprise-grade security and performance. The API handles transaction processing, budget management, and financial analytics.\n\nBuilt with security-first principles, it features OAuth2 authentication, data encryption, and comprehensive audit logging. The API supports multiple banking integrations and provides real-time transaction processing with fraud detection capabilities.`,
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
      ],
      techStack: ["Python", "FastAPI", "PostgreSQL", "Redis", "Celery", "Docker", "AWS"],
      type: "API",
      industry: "Finance",
      status: "Live",
      year: "2023",
      featured: false,
      liveUrl: "https://api.financetracker.com",
      githubUrl: "https://github.com/lahiru/finance-api",
      metrics: [
        { label: "API Calls/Day", value: "100K+" },
        { label: "Response Time", value: "<50ms" },
        { label: "Uptime", value: "99.99%" },
        { label: "Transactions Processed", value: "$2M+" }
      ],
      features: [
        "RESTful API design",
        "OAuth2 authentication",
        "Real-time transaction processing",
        "Banking API integrations",
        "Fraud detection algorithms",
        "Comprehensive documentation"
      ],
      highlights: [
        "Processed over $2M in transactions",
        "Achieved 99.99% uptime",
        "Integrated with 10+ banking APIs",
        "Zero security breaches"
      ],
      architecture: `Python FastAPI framework with PostgreSQL for data persistence and Redis for caching. Celery handles background tasks, Docker containers ensure consistent deployment, and AWS provides scalable infrastructure.`,
      challenges: [
        {
          problem: "Ensuring PCI DSS compliance for financial data processing",
          solution: "Implemented comprehensive security measures including data encryption, secure key management, and regular security audits"
        },
        {
          problem: "Handling high-frequency transaction processing without data inconsistency",
          solution: "Designed atomic transaction processing with database locks and implemented eventual consistency patterns for non-critical operations"
        }
      ],
      testimonials: [
        {
          name: "David Kim",
          role: "Fintech Startup Founder",
          avatar: "https://randomuser.me/api/portraits/men/28.jpg",
          content: "The API is incredibly reliable and well-documented. It's been the backbone of our fintech application.",
          rating: 5
        }
      ]
    },
    {
      id: 6,
      title: "EduLearn LMS",
      description: "Learning Management System with interactive courses, progress tracking, and collaborative learning features.",
      fullDescription: `EduLearn LMS is a comprehensive learning management system designed for educational institutions and corporate training programs. The platform supports various content types, assessment tools, and collaborative learning features.\n\nThe system features adaptive learning paths, real-time progress tracking, and integrated video conferencing. Built with scalability in mind, it can handle thousands of concurrent learners while providing personalized learning experiences.`,
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&h=600&fit=crop"
      ],
      techStack: ["Angular", "Spring Boot", "MySQL", "WebRTC", "AWS S3", "Elasticsearch"],
      type: "Web App",
      industry: "Education",
      status: "In Development",
      year: "2024",
      featured: false,
      githubUrl: "https://github.com/lahiru/edulearn-lms",
      metrics: [
        { label: "Students Enrolled", value: "5K+" },
        { label: "Courses Available", value: "200+" },
        { label: "Completion Rate", value: "78%" },
        { label: "Instructor Rating", value: "4.6★" }
      ],
      features: [
        "Interactive course builder",
        "Real-time progress tracking",
        "Video conferencing integration",
        "Assessment and grading tools",
        "Discussion forums",
        "Mobile-responsive design"
      ],
      highlights: [
        "Serving 5000+ students across 50+ institutions",
        "Achieved 78% course completion rate",
        "Integrated with popular video conferencing tools",
        "Supports 15+ content formats"
      ],
      architecture: `Angular frontend with Spring Boot backend services. MySQL for data storage, WebRTC for video communications, AWS S3 for content delivery, and Elasticsearch for course search functionality.`,
      challenges: [
        {
          problem: "Delivering high-quality video content to users with varying internet speeds",
          solution: "Implemented adaptive bitrate streaming and CDN integration to ensure optimal video quality for all users"
        },
        {
          problem: "Creating engaging interactive learning experiences",
          solution: "Developed gamification features, interactive quizzes, and collaborative tools to increase student engagement"
        }
      ],
      testimonials: [
        {
          name: "Prof. Lisa Thompson",
          role: "Education Technology Director",
          avatar: "https://randomuser.me/api/portraits/women/38.jpg",
          content: "The LMS has transformed our online learning capabilities. Students are more engaged than ever.",
          rating: 5
        }
      ]
    }
  ];

  // Filter and search logic
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.techStack.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply category filters
    Object.entries(activeFilters).forEach(([category, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter(project => {
          switch (category) {
            case 'technology':
              return values.some(tech => project.techStack.includes(tech));
            case 'type':
              return values.includes(project.type);
            case 'industry':
              return values.includes(project.industry);
            case 'status':
              return values.includes(project.status);
            default:
              return true;
          }
        });
      }
    });

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        return filtered.sort((a, b) => b.year.localeCompare(a.year));
      case 'oldest':
        return filtered.sort((a, b) => a.year.localeCompare(b.year));
      case 'name':
        return filtered.sort((a, b) => a.title.localeCompare(b.title));
      default:
        return filtered;
    }
  }, [projects, searchQuery, activeFilters, sortBy]);

  // Generate filter options
  const filterOptions = useMemo(() => {
    const technologies = [...new Set(projects.flatMap(p => p.techStack))].map(tech => ({
      value: tech,
      label: tech,
      count: projects.filter(p => p.techStack.includes(tech)).length
    }));

    const types = [...new Set(projects.map(p => p.type))].map(type => ({
      value: type,
      label: type,
      count: projects.filter(p => p.type === type).length
    }));

    const industries = [...new Set(projects.map(p => p.industry))].map(industry => ({
      value: industry,
      label: industry,
      count: projects.filter(p => p.industry === industry).length
    }));

    const statuses = [...new Set(projects.map(p => p.status))].map(status => ({
      value: status,
      label: status,
      count: projects.filter(p => p.status === status).length
    }));

    return {
      technologies: technologies.sort((a, b) => b.count - a.count),
      types: types.sort((a, b) => b.count - a.count),
      industries: industries.sort((a, b) => b.count - a.count),
      statuses: statuses.sort((a, b) => b.count - a.count)
    };
  }, [projects]);

  const featuredProject = projects.find(p => p.featured);

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleClearFilters = () => {
    setActiveFilters({
      technology: [],
      type: [],
      industry: [],
      status: []
    });
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Projects - Real-World Solutions | Lahiru Senavirathna</title>
        <meta name="description" content="Explore Lahiru Senavirathna's portfolio of 14+ innovative projects including AI/ML applications, mobile apps, web platforms, and enterprise systems across healthcare, education, and e-commerce industries." />
        <meta name="keywords" content="projects, portfolio, AI, machine learning, Flutter, React, Python, full-stack development, mobile apps, web applications, healthcare tech, education tech" />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-brand-purple/5 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-2 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="Rocket" size={24} className="text-primary" />
                </div>
                <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                  Innovation Laboratory
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-text-primary mb-6">
                Real-World Projects That <span className="gradient-text">Solve Real Problems</span>
              </h1>
              
              <p className="text-xl text-text-secondary leading-relaxed mb-8">
                Explore my portfolio of 14+ innovative solutions spanning AI/ML applications, mobile apps, 
                web platforms, and enterprise systems. Each project demonstrates technical excellence, problem-solving skills,
                and real-world impact across healthcare, education, e-commerce, and more industries.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => document.getElementById('projects-grid').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Projects
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Github"
                  iconPosition="left"
                  onClick={() => window.open('https://github.com/lahiru', '_blank')}
                >
                  View GitHub
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Project Statistics */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <ProjectStats projects={projects} />
          </div>
        </section>

        {/* Featured Project */}
        {featuredProject && (
          <section className="py-16 bg-surface/50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                  Featured Project Spotlight
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Highlighting my most impactful project that showcases advanced technical skills 
                  and delivers exceptional user value.
                </p>
              </div>
              
              <FeaturedProject 
                project={featuredProject} 
                onViewDetails={handleViewDetails}
              />
            </div>
          </section>
        )}

        {/* Projects Grid Section */}
        <section id="projects-grid" className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                Complete Project Portfolio
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Browse through my comprehensive collection of projects, filter by technology or industry, 
                and discover the technical solutions I've built.
              </p>
            </div>

            {/* Filters */}
            <ProjectFilters
              filters={filterOptions}
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onClearFilters={handleClearFilters}
              projectCount={filteredProjects.length}
            />

            {/* Sort Options */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-2">
                <Icon name="ArrowUpDown" size={16} className="text-text-muted" />
                <span className="text-sm font-medium text-text-secondary">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm bg-surface border border-border rounded-lg px-3 py-1 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name">Alphabetical</option>
                </select>
              </div>

              <div className="text-sm text-text-muted">
                Showing {filteredProjects.length} of {projects.length} projects
              </div>
            </div>

            {/* Projects Grid */}
            {filteredProjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Search" size={32} className="text-text-muted" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">No Projects Found</h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your search criteria or clearing the filters to see more projects.
                </p>
                <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={handleClearFilters}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gradient-to-br from-primary/5 via-accent/5 to-brand-purple/5">
          <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon name="MessageCircle" size={32} className="text-primary" />
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Ready to Build Something Amazing?
            </h2>
            
            <p className="text-lg text-text-secondary mb-8">
              Let's discuss your next project and explore how my technical expertise 
              can help bring your vision to life.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Mail"
                iconPosition="left"
              >
                Send Message
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />


    </div>
  );
};

export default ProjectsInnovationLaboratory;